import { useState } from 'react';
import { FiBriefcase, FiChevronDown, FiHelpCircle, FiLogOut, FiSettings } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts';
import { FreePlanBanner, FreePlanChip } from '../../FreePlan';
import { usePreferences } from '../../../contexts/PreferencesContext';
import { NotificationBell } from './NotificationBell';
import styles from './Header.module.css';

interface HeaderProps {
    title?: string;
    subtitle?: string;
    ocultarSeletorFilial?: boolean;
}

export function Header({ title, subtitle, ocultarSeletorFilial }: HeaderProps) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, filialAtiva, filiais, hasFeature } = useAuth();
    const { t } = usePreferences();

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Na visao consolidada o rotulo do seletor troca de "Negocio" para
    // "Somente visualizacao": o usuario precisa saber, antes de procurar o
    // botao de lancar, que dali nao se lanca.
    const somenteVisualizacao = filialAtiva?.id === 'all';

    const filialLabel = !filialAtiva
        ? t('header.selecionarNegocio')
        : filialAtiva.id === 'all'
            ? t('header.todosNegocios')
            : filialAtiva.name;

    return (
        <header className={styles.header}>
            <div className={styles.leftContent}>
                {title && (
                    <div className={styles.titleGroup}>
                        <h1 className={styles.title}>{title}</h1>
                        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
                    </div>
                )}
                {/* MAN-364: no celular a barra do Gratuito vira este selo ao lado do título. */}
                <FreePlanChip />
            </div>

            {/* MAN-364: barra do plano Gratuito na faixa do topo, entre o título e as ações. */}
            <FreePlanBanner />

            <div className={styles.actions}>
                {!ocultarSeletorFilial && hasFeature('filiais') && filiais.length > 0 && (
                    <button
                        className={styles.filialSection}
                        onClick={() => navigate('/select-filial', { state: { from: location.pathname + location.search } })}
                        title={t('header.trocarNegocio')}
                    >
                        <div className={styles.filialIcon}>
                            <FiBriefcase size={16} />
                        </div>
                        <div className={styles.filialInfo}>
                            <span className={`${styles.filialLabel} ${somenteVisualizacao ? styles.filialLabelVisualizacao : ''}`}>
                                {somenteVisualizacao ? t('header.somenteVisualizacao') : t('header.negocio')}
                            </span>
                            <span className={styles.filialName} title={filialLabel}>{filialLabel}</span>
                        </div>
                        <FiChevronDown className={styles.filialChevron} />
                    </button>
                )}

                <NotificationBell />

                <button className={styles.helpBtn} onClick={() => navigate('/help')} title="Ajuda">
                    <FiHelpCircle size={20} />
                </button>

                <div className={styles.avatarWrapper}>
                    <button
                        className={styles.avatar}
                        onClick={() => setDropdownOpen((prev) => !prev)}
                    >
                        {user ? getInitials(user.name) : 'US'}
                    </button>

                    {dropdownOpen && (
                        <>
                            <div className={styles.backdrop} onClick={() => setDropdownOpen(false)} />
                            <div className={styles.dropdown}>
                                <button
                                    className={styles.dropdownItem}
                                    onClick={() => { setDropdownOpen(false); navigate('/settings'); }}
                                >
                                    <FiSettings size={15} />
                                    {t('header.configuracoes')}
                                </button>
                                <button
                                    className={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                                    onClick={() => { setDropdownOpen(false); logout(); }}
                                >
                                    <FiLogOut size={15} />
                                    {t('header.sair')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}
